import { useMemo } from "react";
import { ComboboxGroupByValue } from "./ComboboxGroupContext";

export function useGroups<Item>({
  items,
  groupBy,
  groupToString,
  groupSorter = (a, b) => `${a}`.localeCompare(`${b}`),
}: {
  items: Item[];
  groupBy?: string;
  groupToString?: (key: string) => string | null;
  groupSorter?: (a: ComboboxGroupByValue, b: ComboboxGroupByValue) => number;
}) {
  const { groups, orderedItems } = useMemo(() => {
    if (!groupBy) return { groups: [], orderedItems: items };
    const groupsSansItems: {
      key: ComboboxGroupByValue;
      label: string | null;
    }[] = []; // { key: "strawberry", label: "Strawberry" }
    const groupedItems: Record<ComboboxGroupByValue, Item[]> = {}; // { strawberry: [] }

    items.forEach((item) => {
      const groupKey = (item as Record<string, any>)[groupBy];
      if (!groupedItems[groupKey]) {
        groupedItems[groupKey] = [];
      }
      groupedItems[groupKey].push(item);
    });

    Object.keys(groupedItems).forEach((key) => {
      groupsSansItems.push({
        key,
        label: groupToString ? groupToString?.(key) : key,
      });
    });

    // Sort groups
    // Yes, this is mutative. We'll survive.
    groupsSansItems.sort((a, b) => groupSorter(a.key, b.key));

    const orderedItems = [] as Item[];
    // [{ key: "strawberry", label: "Strawberry", items: [{...}, {...}, {...}] }]
    const groups = groupsSansItems.map((group) => ({
      ...group,
      items: groupedItems[group.key].map((item) => {
        orderedItems.push(item);
        return item;
      }),
    }));

    return { groups, orderedItems };
  }, [groupBy, groupSorter, groupToString, items]);

  return { groups, orderedItems };
}
